/**
 * This file was auto-generated by Fern from our API Definition.
 */

import * as core from "../../../../core/index.js";
import * as LangfuseAPI from "../../../index.js";
import {
  mergeHeaders,
  mergeOnlyDefinedHeaders,
} from "../../../../core/headers.js";
import * as errors from "../../../../errors/index.js";

export declare namespace ScoreV2 {
  export interface Options {
    environment: core.Supplier<string>;
    /** Specify a custom URL to connect the client to. */
    baseUrl?: core.Supplier<string>;
    username?: core.Supplier<string | undefined>;
    password?: core.Supplier<string | undefined>;
    /** Override the X-Langfuse-Sdk-Name header */
    xLangfuseSdkName?: core.Supplier<string | undefined>;
    /** Override the X-Langfuse-Sdk-Version header */
    xLangfuseSdkVersion?: core.Supplier<string | undefined>;
    /** Override the X-Langfuse-Public-Key header */
    xLangfusePublicKey?: core.Supplier<string | undefined>;
    /** Additional headers to include in requests. */
    headers?: Record<
      string,
      string | core.Supplier<string | null | undefined> | null | undefined
    >;
  }

  export interface RequestOptions {
    /** The maximum time to wait for a response in seconds. */
    timeoutInSeconds?: number;
    /** The number of times to retry the request. Defaults to 2. */
    maxRetries?: number;
    /** A hook to abort the request. */
    abortSignal?: AbortSignal;
    /** Override the X-Langfuse-Sdk-Name header */
    xLangfuseSdkName?: string | undefined;
    /** Override the X-Langfuse-Sdk-Version header */
    xLangfuseSdkVersion?: string | undefined;
    /** Override the X-Langfuse-Public-Key header */
    xLangfusePublicKey?: string | undefined;
    /** Additional query string parameters to include in the request. */
    queryParams?: Record<string, unknown>;
    /** Additional headers to include in the request. */
    headers?: Record<
      string,
      string | core.Supplier<string | null | undefined> | null | undefined
    >;
  }
}

export class ScoreV2 {
  protected readonly _options: ScoreV2.Options;

  constructor(_options: ScoreV2.Options) {
    this._options = _options;
  }

  /**
   * Get a list of scores (supports both trace and session scores)
   *
   * @param {LangfuseAPI.GetScoresRequest} request
   * @param {ScoreV2.RequestOptions} requestOptions - Request-specific configuration.
   *
   * @throws {@link LangfuseAPI.Error}
   * @throws {@link LangfuseAPI.UnauthorizedError}
   * @throws {@link LangfuseAPI.AccessDeniedError}
   * @throws {@link LangfuseAPI.MethodNotAllowedError}
   * @throws {@link LangfuseAPI.NotFoundError}
   *
   * @example
   *     await client.scoreV2.get()
   */
  public get(
    request: LangfuseAPI.GetScoresRequest = {},
    requestOptions?: ScoreV2.RequestOptions,
  ): core.HttpResponsePromise<LangfuseAPI.GetScoresResponse> {
    return core.HttpResponsePromise.fromPromise(
      this.__get(request, requestOptions),
    );
  }

  private async __get(
    request: LangfuseAPI.GetScoresRequest = {},
    requestOptions?: ScoreV2.RequestOptions,
  ): Promise<core.WithRawResponse<LangfuseAPI.GetScoresResponse>> {
    const {
      page,
      limit,
      userId,
      name,
      fromTimestamp,
      toTimestamp,
      environment,
      source,
      operator,
      value,
      scoreIds,
      configId,
      sessionId,
      datasetRunId,
      traceId,
      observationId,
      queueId,
      dataType,
      traceTags,
      fields,
    } = request;
    const _queryParams: Record<
      string,
      string | string[] | object | object[] | null
    > = {};
    if (page != null) {
      _queryParams["page"] = page.toString();
    }

    if (limit != null) {
      _queryParams["limit"] = limit.toString();
    }

    if (userId != null) {
      _queryParams["userId"] = userId;
    }

    if (name != null) {
      _queryParams["name"] = name;
    }

    if (fromTimestamp != null) {
      _queryParams["fromTimestamp"] = fromTimestamp;
    }

    if (toTimestamp != null) {
      _queryParams["toTimestamp"] = toTimestamp;
    }

    if (environment != null) {
      if (Array.isArray(environment)) {
        _queryParams["environment"] = environment.map((item) => item);
      } else {
        _queryParams["environment"] = environment;
      }
    }

    if (source != null) {
      _queryParams["source"] = source;
    }

    if (operator != null) {
      _queryParams["operator"] = operator;
    }

    if (value != null) {
      _queryParams["value"] = value.toString();
    }

    if (scoreIds != null) {
      _queryParams["scoreIds"] = scoreIds;
    }

    if (configId != null) {
      _queryParams["configId"] = configId;
    }

    if (sessionId != null) {
      _queryParams["sessionId"] = sessionId;
    }

    if (datasetRunId != null) {
      _queryParams["datasetRunId"] = datasetRunId;
    }

    if (traceId != null) {
      _queryParams["traceId"] = traceId;
    }

    if (observationId != null) {
      _queryParams["observationId"] = observationId;
    }

    if (queueId != null) {
      _queryParams["queueId"] = queueId;
    }

    if (dataType != null) {
      _queryParams["dataType"] = dataType;
    }

    if (traceTags != null) {
      if (Array.isArray(traceTags)) {
        _queryParams["traceTags"] = traceTags.map((item) => item);
      } else {
        _queryParams["traceTags"] = traceTags;
      }
    }

    if (fields != null) {
      _queryParams["fields"] = fields;
    }

    let _headers: core.Fetcher.Args["headers"] = mergeHeaders(
      this._options?.headers,
      mergeOnlyDefinedHeaders({
        Authorization: await this._getAuthorizationHeader(),
        "X-Langfuse-Sdk-Name":
          requestOptions?.xLangfuseSdkName ?? this._options?.xLangfuseSdkName,
        "X-Langfuse-Sdk-Version":
          requestOptions?.xLangfuseSdkVersion ??
          this._options?.xLangfuseSdkVersion,
        "X-Langfuse-Public-Key":
          requestOptions?.xLangfusePublicKey ??
          this._options?.xLangfusePublicKey,
      }),
      requestOptions?.headers,
    );
    const _response = await core.fetcher({
      url: core.url.join(
        (await core.Supplier.get(this._options.baseUrl)) ??
          (await core.Supplier.get(this._options.environment)),
        "/api/public/v2/scores",
      ),
      method: "GET",
      headers: _headers,
      queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
      timeoutMs:
        requestOptions?.timeoutInSeconds != null
          ? requestOptions.timeoutInSeconds * 1000
          : 60000,
      maxRetries: requestOptions?.maxRetries,
      abortSignal: requestOptions?.abortSignal,
    });
    if (_response.ok) {
      return {
        data: _response.body as LangfuseAPI.GetScoresResponse,
        rawResponse: _response.rawResponse,
      };
    }

    if (_response.error.reason === "status-code") {
      switch (_response.error.statusCode) {
        case 400:
          throw new LangfuseAPI.Error(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 401:
          throw new LangfuseAPI.UnauthorizedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 403:
          throw new LangfuseAPI.AccessDeniedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 405:
          throw new LangfuseAPI.MethodNotAllowedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 404:
          throw new LangfuseAPI.NotFoundError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        default:
          throw new errors.LangfuseAPIError({
            statusCode: _response.error.statusCode,
            body: _response.error.body,
            rawResponse: _response.rawResponse,
          });
      }
    }

    switch (_response.error.reason) {
      case "non-json":
        throw new errors.LangfuseAPIError({
          statusCode: _response.error.statusCode,
          body: _response.error.rawBody,
          rawResponse: _response.rawResponse,
        });
      case "timeout":
        throw new errors.LangfuseAPITimeoutError(
          "Timeout exceeded when calling GET /api/public/v2/scores.",
        );
      case "unknown":
        throw new errors.LangfuseAPIError({
          message: _response.error.errorMessage,
          rawResponse: _response.rawResponse,
        });
    }
  }

  /**
   * Get a score (supports both trace and session scores)
   *
   * @param {string} scoreId - The unique langfuse identifier of a score
   * @param {ScoreV2.RequestOptions} requestOptions - Request-specific configuration.
   *
   * @throws {@link LangfuseAPI.Error}
   * @throws {@link LangfuseAPI.UnauthorizedError}
   * @throws {@link LangfuseAPI.AccessDeniedError}
   * @throws {@link LangfuseAPI.MethodNotAllowedError}
   * @throws {@link LangfuseAPI.NotFoundError}
   *
   * @example
   *     await client.scoreV2.getById("scoreId")
   */
  public getById(
    scoreId: string,
    requestOptions?: ScoreV2.RequestOptions,
  ): core.HttpResponsePromise<LangfuseAPI.Score> {
    return core.HttpResponsePromise.fromPromise(
      this.__getById(scoreId, requestOptions),
    );
  }

  private async __getById(
    scoreId: string,
    requestOptions?: ScoreV2.RequestOptions,
  ): Promise<core.WithRawResponse<LangfuseAPI.Score>> {
    let _headers: core.Fetcher.Args["headers"] = mergeHeaders(
      this._options?.headers,
      mergeOnlyDefinedHeaders({
        Authorization: await this._getAuthorizationHeader(),
        "X-Langfuse-Sdk-Name":
          requestOptions?.xLangfuseSdkName ?? this._options?.xLangfuseSdkName,
        "X-Langfuse-Sdk-Version":
          requestOptions?.xLangfuseSdkVersion ??
          this._options?.xLangfuseSdkVersion,
        "X-Langfuse-Public-Key":
          requestOptions?.xLangfusePublicKey ??
          this._options?.xLangfusePublicKey,
      }),
      requestOptions?.headers,
    );
    const _response = await core.fetcher({
      url: core.url.join(
        (await core.Supplier.get(this._options.baseUrl)) ??
          (await core.Supplier.get(this._options.environment)),
        `/api/public/v2/scores/${encodeURIComponent(scoreId)}`,
      ),
      method: "GET",
      headers: _headers,
      queryParameters: requestOptions?.queryParams,
      timeoutMs:
        requestOptions?.timeoutInSeconds != null
          ? requestOptions.timeoutInSeconds * 1000
          : 60000,
      maxRetries: requestOptions?.maxRetries,
      abortSignal: requestOptions?.abortSignal,
    });
    if (_response.ok) {
      return {
        data: _response.body as LangfuseAPI.Score,
        rawResponse: _response.rawResponse,
      };
    }

    if (_response.error.reason === "status-code") {
      switch (_response.error.statusCode) {
        case 400:
          throw new LangfuseAPI.Error(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 401:
          throw new LangfuseAPI.UnauthorizedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 403:
          throw new LangfuseAPI.AccessDeniedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 405:
          throw new LangfuseAPI.MethodNotAllowedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 404:
          throw new LangfuseAPI.NotFoundError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        default:
          throw new errors.LangfuseAPIError({
            statusCode: _response.error.statusCode,
            body: _response.error.body,
            rawResponse: _response.rawResponse,
          });
      }
    }

    switch (_response.error.reason) {
      case "non-json":
        throw new errors.LangfuseAPIError({
          statusCode: _response.error.statusCode,
          body: _response.error.rawBody,
          rawResponse: _response.rawResponse,
        });
      case "timeout":
        throw new errors.LangfuseAPITimeoutError(
          "Timeout exceeded when calling GET /api/public/v2/scores/{scoreId}.",
        );
      case "unknown":
        throw new errors.LangfuseAPIError({
          message: _response.error.errorMessage,
          rawResponse: _response.rawResponse,
        });
    }
  }

  protected async _getAuthorizationHeader(): Promise<string | undefined> {
    const username = await core.Supplier.get(this._options.username);
    const password = await core.Supplier.get(this._options.password);
    if (username != null && password != null) {
      return core.BasicAuth.toAuthorizationHeader({
        username: username,
        password: password,
      });
    }

    return undefined;
  }
}
